import React, { useEffect, useState } from 'react';

const TaskModal = ({ task, onClose, onSave, isEdit }) => {
  const [form, setForm] = useState({
    name: task?.name || '',
    projectId: task?.projectId || '',
    status: task?.status || 'pending',
  });
  const [loading, setLoading] = useState(false);
  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    await onSave(form);
    setLoading(false);
    onClose();
  };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(8px)', padding: '1rem' }} onClick={onClose}>
      <div style={{ position: 'relative', padding: '2rem', background: 'var(--color-bg)', width: '100%', maxWidth: 480, margin: 'auto', borderRadius: 'var(--border-radius)', boxShadow: 'var(--box-shadow)', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-primary)', marginBottom: '1rem' }}>{isEdit ? 'Edit Task' : 'Add Task'}</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, color: 'var(--color-text)', marginBottom: 4 }}>Task Name</label>
            <input name="name" type="text" style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', color: 'var(--color-text)', background: 'var(--color-bg)', fontSize: '1rem' }} value={form.name} onChange={handleChange} required />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, color: 'var(--color-text)', marginBottom: 4 }}>Project ID</label>
            <input name="projectId" type="text" style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', color: 'var(--color-text)', background: 'var(--color-bg)', fontSize: '1rem' }} value={form.projectId} onChange={handleChange} required />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, color: 'var(--color-text)', marginBottom: 4 }}>Status</label>
            <select name="status" style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', color: 'var(--color-text)', background: 'var(--color-bg)', fontSize: '1rem' }} value={form.status} onChange={handleChange}>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
            <button type="button" onClick={onClose} style={{ background: 'var(--color-secondary)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.5rem 1.25rem', fontSize: '1rem', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ background: 'var(--color-primary)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.5rem 1.25rem', fontSize: '1rem', cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>{loading ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Task')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);

  // Fetch tasks and projects from backend
  const fetchTasks = async () => {
    setLoading(true);
    setError('');
    try {
      const [tasksRes, projectsRes] = await Promise.all([
        fetch('/api/get-tasks.php'),
        fetch('/api/get-projects.php'),
      ]);
      const tasksData = await tasksRes.json();
      const projectsData = await projectsRes.json();
      setTasks(Array.isArray(tasksData) ? tasksData : (tasksData.tasks || []));
      setProjects(Array.isArray(projectsData) ? projectsData : (projectsData.projects || []));
    } catch (err) {
      setError('Failed to load tasks.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  // Add task
  const handleAddTask = async (taskData) => {
    const res = await fetch('/api/add-task.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(taskData)
    });
    if (!res.ok) throw new Error('Failed to add task');
    await fetchTasks();
  };

  // Edit task
  const handleEditTask = async (editData) => {
    const res = await fetch('/api/edit-task.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: editTask.id, ...editData })
    });
    if (!res.ok) throw new Error('Failed to edit task');
    await fetchTasks();
  };

  // Delete task
  const handleDeleteTask = async (taskId) => {
    const res = await fetch('/api/delete-task.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: taskId })
    });
    if (!res.ok) throw new Error('Failed to delete task');
    await fetchTasks();
  };

  // Helper to get project name
  const getProjectName = (projectId) => {
    const project = projects.find(p => String(p.id) === String(projectId));
    return project ? project.name : projectId;
  };

  return (
    <div style={{ padding: 'var(--section-padding)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--color-primary)', fontSize: '2rem', fontWeight: 700 }}>Tasks</h2>
        <button
          style={{
            background: 'var(--color-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--border-radius)',
            padding: '0.5rem 1.5rem',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: 'var(--box-shadow)'
          }}
          onClick={() => { setEditTask(null); setShowModal(true); }}
        >
          + Add Task
        </button>
      </div>
      {loading ? (
        <div>Loading tasks...</div>
      ) : error ? (
        <div style={{ color: 'var(--color-error)' }}>{error}</div>
      ) : (
        <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--border-radius)', boxShadow: 'var(--box-shadow)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-alt)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--color-text-light)' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--color-text-light)' }}>Project</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--color-text-light)' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--color-text-light)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-light)' }}>No tasks found.</td></tr>
              ) : (
                tasks.map(task => (
                  <tr key={task.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '1rem', color: 'var(--color-text)' }}>{task.name}</td>
                    <td style={{ padding: '1rem', color: 'var(--color-text)' }}>{getProjectName(task.projectId)}</td>
                    <td style={{ padding: '1rem', color: 'var(--color-text)' }}>{task.status}</td>
                    <td style={{ padding: '1rem' }}>
                      <button
                        style={{ background: 'var(--color-secondary)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.25rem 1rem', marginRight: 8, cursor: 'pointer' }}
                        onClick={() => { setEditTask(task); setShowModal(true); }}
                      >
                        Edit
                      </button>
                      <button
                        style={{ background: 'var(--color-error)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.25rem 1rem', cursor: 'pointer' }}
                        onClick={() => handleDeleteTask(task.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {showModal && (
        <TaskModal
          task={editTask}
          isEdit={!!editTask}
          onClose={() => setShowModal(false)}
          onSave={editTask ? handleEditTask : handleAddTask}
        />
      )}
    </div>
  );
};

export default TasksPage; 